import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { UserRoleSelect } from 'src/app/account/membersPage/roleSelect/UserRoleSelect';
import { Form } from 'src/components/common/form/Form';
import { Typeahead } from 'src/components/common/typeahead/Typeahead';
import { FullNameColumn } from 'src/components/fullNameColumn/FullNameColumn';
import Config from 'src/lib/config';
import { UserRoleOption } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

export const AddHiddenGraphMemberModalMembershipFragment = gql`
  fragment AddHiddenGraphMemberModalMembershipFragment on AccountMembership {
    createdAt
    role: permission
    user {
      id
      avatarUrl
      fullName
      email
    }
    account {
      id
    }
  }
`;
interface Props {
  isOpen: boolean;
  onClose: () => void;
  members: GraphQLTypes.AddHiddenGraphMemberModalMembershipFragment[];
  graphId: string;
  accountId: string;
  availableRoles: UserPermission[];
}

export const AddHiddenGraphMemberModal = ({
  isOpen,
  onClose,
  members,
  graphId,
  accountId,
  availableRoles,
}: Props) => {
  const [addMember, { loading }] = useMutation<
    GraphQLTypes.AddHiddenGraphMemberMutation,
    GraphQLTypes.AddHiddenGraphMemberMutationVariables
  >(
    gql`
      mutation AddHiddenGraphMemberMutation(
        $graphId: ID!
        $userID: ID!
        $role: UserPermission
      ) {
        service(id: $graphId) {
          overrideUserPermission(userID: $userID, permission: $role) {
            id
            roleOverrides {
              role
              user {
                id
              }
            }
          }
        }
      }
    `,
    { onCompleted: () => onClose() },
  );

  const [userRole, setUserRole] = useState<UserRoleOption>(
    GraphQLTypes.UserPermission.CONSUMER,
  );
  const [selectedUserId, setSelectedUserId] = useState<string>();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Form
        onSubmit={async () => {
          if (!selectedUserId) return;
          await addMember({
            variables: {
              graphId,
              userID: selectedUserId,
              role: userRole,
            },
          });
        }}
        noValidate={true}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Who would you like to add to this graph?</ModalTitle>
          </ModalHeader>
          <ModalBody className="z-[2]" isScrollable={false}>
            <div className="flex gap-4">
              <Typeahead
                className="w-120"
                disabled={loading}
                onSelected={(m) => {
                  setSelectedUserId(m.user.id);
                }}
                placeholder="Select a user"
                options={members}
                getId={(m) => m.user.id}
                getSearchString={(m) => m.user.fullName}
                renderToString={(m) => m.user.fullName}
                renderToListItem={(highlightedSearchString, m) => (
                  <FullNameColumn
                    ghUsername={null}
                    accountId={accountId}
                    avatarUrl={m.user.avatarUrl}
                    fullName={
                      <>
                        {highlightedSearchString} (
                        {Config.roleNameByRoleEnum[m.role]})
                      </>
                    }
                    email={m.user.email}
                  />
                )}
              />

              <UserRoleSelect
                target="user-in-org"
                className="flex-1"
                userRole={userRole}
                setUserRole={setUserRole}
                options={Config.orderedUserOverrideRoles}
                placement="bottom-end"
                maxWidth="30vw"
                availableRoles={availableRoles}
                matchTriggerWidth={false}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Link isExternal to={DocsRouter.path('GraphRoles')}>
              Learn more about roles
            </Link>
            <ButtonGroup>
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={!selectedUserId}
                isLoading={loading}
                loadingText="Add member"
              >
                Add member
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
